<!-- @format -->

<template>
    <div class="gst-order-confirmation d-flex px-2 mb-8">
        <OrderConfirmationRokt
            v-if="isHotelOnlyFlow"
            :order="order" />
        <div class="gst-order-confirmation__content d-flex">
            <div class="gst-order-confirmation__header-container d-flex">
                <span class="title">{{ title }}</span>
                <span
                    v-if="isExperienceOffer && !hotelReservationOrderNumber"
                    class="subtitle">
                    {{ $t('_common:terms.order') }} #{{ eventTicketOrderNumber }}
                </span>
                <template v-else>
                    <span
                        v-if="hasTickets"
                        class="subtitle">
                        {{ $t('ticketsOrder') }} #{{ eventTicketOrderNumber }}
                    </span>
                    <span
                        v-if="hasHotelReservation"
                        class="subtitle hotel-number">
                        {{ $t('hotelOrder') }} #{{ hotelReservationOrderNumber }}
                    </span>
                </template>
            </div>
            <div class="gst-order-confirmation__detail d-flex flex-column align-center">
                <div
                    v-if="userIsAuth"
                    class="gst-order-confirmation_earn d-flex flex-column">
                    <EarnBanner :points="pointsEarnedAsNumber">
                        <template #default="{ points }">
                            <div
                                class="gst-order-confirmation_earn-banner-content d-flex flex-column align-center">
                                <span class="gst-order-confirmation_earn-banner-content_title">{{
                                    $t(
                                        'shared:components.bonusPoints.bonusPointsCartCheckoutConfirmation.title'
                                    )
                                }}</span>
                                <span class="gst-order-confirmation_earn-banner-content_points">{{
                                    $t(
                                        'shared:components.bonusPoints.bonusPointsCartCheckoutConfirmation.pointWithCount',
                                        { count: pointsEarnedAsNumber, formattedCount: points }
                                    )
                                }}</span>
                            </div>
                        </template>
                    </EarnBanner>
                    <PointsInformation />
                </div>
                <OrderConfirmationExperienceInfo
                    v-if="isExperienceOffer"
                    v-bind="propsExperience" />
                <template v-else>
                    <OrderConfirmationTicketsInfo
                        v-if="hasTickets"
                        :event="event"
                        :ticket="ticket" />
                    <OrderConfirmationHotelReservationInfo
                        v-if="hasHotelReservation"
                        :event="event"
                        :hotel="hotel"
                        :is-package-offer="isPackageOffer"
                        :has-hotel-reservation-only="hasHotelReservationOnly"
                        :gift-card="giftCard" />
                </template>
                <OrderConfirmationPaymentInfo
                    :currency="currency"
                    :payment-items="paymentItems"
                    :has-hotel-reservation-only="hasHotelReservationOnly"
                    :order="order"
                    :is-package-offer="isPackageOffer"
                    :is-open-inventory-or-dynamic="isOpenInventoryOrDynamic"
                    class="gst-order-confirmation__payment-info" />
                <div
                    v-if="showGoMobileSection"
                    class="gst-order-confirmation__go-mobile d-flex">
                    <MobileTicket />
                    <span>
                        <strong>{{ $t('goMobile') }}</strong>
                        {{ $t('goMobileInfo') }}
                    </span>
                </div>
                <BaseButton
                    v-if="hasTickets"
                    class="gst-btn"
                    :data-test-id="$testId('button.buyAgain')"
                    @click="onClickBuyAgainDo">
                    {{ $t('buyAgainButton.title') }}
                </BaseButton>
            </div>
        </div>
    </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import productsConstants from '@core/utils/constants/products';
import offerConstants from '@core/utils/constants/offer';
import feeConstants from '@core/utils/constants/fee';
import { getRouteToPoint } from '@core/utils/locationMapUtils';
import { isMobileTicket } from '@core/utils/shippingOptionsUtils';
import purchaseConstants from '@tenant/app/utils/constants/purchase';
import EventFlowMixin from '@tenant/app/mixins/EventFlowMixin';
import HtmlHeadMixin from '@tenant/app/mixins/HtmlHeadMixin';
import MobileTicket from '@tenant/app/assets/icons/mobile_ticket.svg';
import PointsInformation from '@tenant/app/components/containerInfo/PointsInformation.vue';
import EarnBanner from '@tenant/app/components/banner/EarnBanner.vue';
import OrderConfirmationTicketsInfo from './TheOrderConfirmation/OrderConfirmationTicketsInfo.vue';
import OrderConfirmationHotelReservationInfo from './TheOrderConfirmation/OrderConfirmationHotelReservationInfo.vue';
import OrderConfirmationPaymentInfo from './TheOrderConfirmation/OrderConfirmationPaymentInfo.vue';
import OrderConfirmationRokt from './TheOrderConfirmation/OrderConfirmationRokt.vue';
import OrderConfirmationExperienceInfo from './TheOrderConfirmation/OrderConfirmationExperienceInfo.vue';

export default {
    name: 'OrderConfirmation',
    components: {
        MobileTicket,
        EarnBanner,
        OrderConfirmationTicketsInfo,
        OrderConfirmationHotelReservationInfo,
        OrderConfirmationPaymentInfo,
        OrderConfirmationRokt,
        OrderConfirmationExperienceInfo,
        PointsInformation,
    },
    mixins: [EventFlowMixin, HtmlHeadMixin],
    i18nOptions: {
        namespaces: 'main',
        keyPrefix: 'views.cart.theOrderConfirmation',
    },
    testIdOptions: {
        keyPrefix: 'orderConfirmation',
    },
    computed: {
        ...mapGetters({
            userIsAuth: 'user/isAuth',
        }),
        routeParams() {
            return this.$route.params;
        },
        order() {
            return this.routeParams.order;
        },
        pointsEarnedAsNumber() {
            return Number(this.order.pointsEarned);
        },
        legacyEventId() {
            return this.routeParams.legacyEventId;
        },
        purchaseFlow() {
            return this.routeParams.purchaseFlow;
        },
        isOpenInventoryOrDynamic() {
            return (
                this.purchaseFlow === purchaseConstants.CART.FLOWS.HOTELS_OPEN_INVENTORY ||
                this.purchaseFlow === purchaseConstants.CART.FLOWS.DYNAMIC
            );
        },
        eventTicket() {
            return this.order.items.find(
                (item) => item.productTypeId === productsConstants.TYPES.TICKET
            );
        },
        eventTicketItems() {
            return this.eventTicket.offers;
        },
        eventTicketOrderNumber() {
            return this.eventTicket.orderNumber;
        },
        hotelReservation() {
            return this.order.items.find(
                (item) => item.productTypeId === productsConstants.TYPES.HOTEL_RESERVATION
            );
        },
        hasHotelReservation() {
            return !!this.hotelReservation;
        },
        hasHotelReservationOnly() {
            return this.hasHotelReservation && this.order.items.length === 1;
        },
        hotelReservationOrderNumber() {
            return this.hotelReservation.orderNumber;
        },
        hasTickets() {
            return !!this.eventTicket;
        },
        packageOffer() {
            return this.order.packageOffer;
        },
        isExperienceOffer() {
            return (
                this.isPackageOffer &&
                this.packageOffer.type === offerConstants.TYPES.EXPERIENCES_PACKAGE
            );
        },
        title() {
            if (this.isExperienceOffer) {
                return this.$t('experienceTitle');
            }
            if (this.hasHotelReservationOnly) {
                return this.$t('hotelOnlyTitle');
            }
            return this.$t('hotelAndTicketsTitle');
        },
        event() {
            if (!this.hasTickets) {
                return null;
            }
            const {
                name,
                imageUrl: image,
                startDateTime,
                endDateTime,
                venueName,
                address,
                hasStartTime,
            } = this.eventTicket.event;
            const directionsUrl = getRouteToPoint({
                street: address.street,
                cityName: address.cityName,
                stateCode: address.stateCode,
                countryCode: address.countryCode,
            });

            return {
                name,
                image,
                startDateTime,
                endDateTime,
                hasStartTime,
                venueName,
                location: {
                    ...address,
                },
                directionsUrl,
            };
        },
        ticket() {
            if (!this.hasTickets) {
                return false;
            }
            const { eventTicket } = this;
            const { ticket } = eventTicket;
            const { row, section, seats } = ticket;

            return {
                packageOfferId: eventTicket.packageOfferId,
                redemptionUrl: eventTicket.redemptionUrl,
                seat: {
                    row,
                    section,
                    seats,
                },
            };
        },
        hotel() {
            if (!this.hasHotelReservation) {
                return null;
            }

            const { hotelReservation } = this;
            const { accommodation, hotel } = hotelReservation;
            const { room } = hotel;

            return {
                name: hotel.name,
                address: hotel.address,
                image: hotel.image,
                accommodation: accommodation,
                roomCancellationPolicy: room.rate.cancellationPolicies,
                roomAmenities: hotel.room.amenities,
                award: hotelReservation.hotel.award,
                currency: this.order.currency,
                roomRate: hotelReservation.basePrice,
                accommodationNights: hotelReservation.accommodation.nights,
                packageOffer: this.packageOffer,
                supplierConfirmationNumber: hotelReservation.supplierConfirmationNumber,
            };
        },
        currency() {
            return this.order.currency;
        },
        paymentItems() {
            let ret = [];

            if (this.hasTickets) {
                const { eventTicketItems } = this;

                const tickets = eventTicketItems.reduce((acc, item) => {
                    acc = [
                        ...acc,
                        {
                            name: item.name,
                            quantity: item.quantity,
                        },
                    ];

                    return acc;
                }, []);

                ret = [...ret, ...tickets];
            }
            if (this.hasHotelReservation) {
                const { hotelReservation } = this;

                ret.push({
                    name: hotelReservation.hotel.room.name.toLowerCase(),
                    quantity: hotelReservation.accommodation.roomsCount,
                });
            }

            return ret;
        },
        showGoMobileSection() {
            if (!this.hasTickets) {
                return false;
            }
            const deliveryInfo =
                this.eventTicket.charges.find((item) => item.type === feeConstants.TYPES.DELIVERY)
                    .info || {};

            return isMobileTicket(deliveryInfo);
        },
        isPackageOffer() {
            return this.ticket && !!this.ticket.packageOfferId;
        },
        propsExperience() {
            const { hotelReservation, eventTicket, packageOffer } = this;
            const { hotel } = hotelReservation;
            const { event } = eventTicket;
            const { address } = event;
            const { billingDetails } = this.order;
            const directionsUrl = getRouteToPoint({
                street: address.address,
                cityName: address.cityName,
                stateCode: address.stateCode,
                countryCode: address.countryCode,
            });

            return {
                name: packageOffer.name,
                imageUrl: packageOffer.imageUrl,
                highlights: packageOffer.highlights,
                hotel: {
                    name: hotel.name,
                    award: hotel.award,
                },
                hotelReservation: {
                    accommodation: hotelReservation.accommodation,
                    room: {
                        name: hotelReservation.hotel.room.name,
                    },
                    orderNumber: hotelReservation.orderNumber,
                },
                event: {
                    address: {
                        venueName: event.venueName,
                        street: address.street,
                        city: address.cityName,
                        stateCode: address.stateCode,
                        country: address.countryCode,
                    },
                    startDate: event.startDateTime,
                    endDate: event.endDateTime,
                    startDateHasTime: event.hasStartTime,
                    directionsUrl,
                },
                eventTicket: eventTicket,
                billingDetails,
            };
        },
        giftCard() {
            return this.order.giftCard;
        },
    },
    methods: {
        ...mapActions({
            updateLanguagesForEvent: 'languages/updateLanguagesForEvent',
        }),
        onClickBuyAgainDo() {
            if (this.isExperienceOffer) {
                this.$router.push({
                    name: 'eventMarketing',
                    params: {
                        id: this.legacyEventId,
                    },
                });
            } else {
                this.$router.push({
                    name: 'eventPackageOffer',
                    params: {
                        id: this.eventTicket.event.id,
                    },
                });
            }
        },
    },
    mounted() {
        if (!this.order) {
            this.$router.back();
            return;
        }
        this.updateDocumentTitle(
            this.$t('main:viewsTitle.theEvent.experiences', {
                package: this.hotelReservation.hotel.name,
            })
        );
        if (this.eventTicket) {
            this.updateLanguagesForEvent(this.eventTicket.legacyEventId);
        }
    },
};
</script>

<style lang="scss">
@import '@scssMixins';
@import '@scssVariables';

.gst-order-confirmation {
    background-color: theme-color('white');
    color: theme-color('quaternary');
    flex-direction: column;
    align-items: center;

    .gst-btn {
        height: 46px;
        width: 80%;
        background-color: theme-color('primary') !important;
        color: theme-color('white') !important;
        text-transform: unset !important;
    }

    .gst-order-confirmation__content {
        padding: 0 20%;
        flex-direction: column;
        align-items: center;
        gap: theme-spacing(6);

        .gst-order-confirmation__detail {
            padding-bottom: 160px;
            gap: theme-spacing(8);

            @include mobile-only {
                gap: theme-spacing(4);
            }
        }
    }

    .gst-order-confirmation__header-container {
        width: 100%;
        padding: 44px;
        margin-top: theme-spacing(7);
        background: theme-color('primary');
        color: white;
        flex-direction: column;
        align-items: center;
        justify-content: center;

        .title {
            line-height: 43px !important;
            margin-bottom: theme-spacing(3);
            font-size: 34px !important;
            font-weight: font-weight('bold');
            font-family: 'AmericanSans', sans-serif !important;
            text-align: center;
        }

        .subtitle {
            line-height: line-height('xxl') !important;
            font-size: font-size('m') !important;
            font-weight: font-weight('bold');
            font-family: 'AmericanSans', sans-serif !important;
            text-align: center;
        }

        .hotel-number {
            margin-top: theme-spacing(2);
        }
    }

    .gst-order-confirmation__payment-info {
        width: 80%;
        gap: theme-spacing(8);

        @include mobile-only {
            margin-top: theme-spacing(4);
        }
    }

    .gst-order-confirmation__go-mobile {
        width: 80%;
        line-height: line-height('xs');
        padding: theme-spacing(4) theme-spacing(6);
        background: theme-color('quinary');
        color: theme-color('quaternary');
        font-size: font-size('xxxs');
        border-radius: 1px;
        align-items: center;

        span {
            margin-left: theme-spacing(3);
        }
    }

    img {
        width: 100%;
    }

    .gst-order-confirmation_earn {
        width: 100%;
        gap: theme-spacing(4);

        .gst-order-confirmation_earn-banner-content {
            font-weight: font-weight('medium');
            gap: theme-spacing(1);

            .gst-order-confirmation_earn-banner-content_title {
                font-size: font-size('s');
                letter-spacing: -0.25px;
            }

            .gst-order-confirmation_earn-banner-content_points {
                font-size: font-size('xxxl');
                letter-spacing: -0.357px;
            }
        }

        @include desktop-only {
            max-width: 80%;
            gap: theme-spacing(8);
        }
    }
}

@include mobile-only {
    .gst-order-confirmation {
        .gst-order-confirmation__content {
            padding: 0 !important;
        }

        .gst-btn {
            height: 46px;
            width: 95%;
            background-color: theme-color('primary') !important;
            color: theme-color('white') !important;
        }

        .gst-order-confirmation__header-container {
            margin-top: theme-spacing(2);

            .title {
                line-height: 30px !important;
                font-size: 24px !important;
            }

            .subtitle {
                line-height: 17px !important;
                font-size: 14px !important;
            }
        }

        .gst-order-confirmation__payment-info,
        .gst-order-confirmation__go-mobile,
        .gst-order-confirmation__things-to-know {
            width: 95% !important;
        }
    }
}
</style>
